/**
 * Bibliothèque de fonctions du tableur (équivalent des fonctions Google Sheets /
 * Excel les plus utilisées). Chaque fonction reçoit les arguments déjà évalués.
 */
import type { CellValue } from './excelFormula';
import { parseCellRef } from './excelModel';

export interface FnArgs {
  /** Toutes les valeurs aplaties (plages développées). */
  values: CellValue[];
  /** Première valeur de chaque argument. */
  args: CellValue[];
  /** Valeurs aplaties par argument. */
  ranges: CellValue[][];
  /** Référence de plage par argument (null si scalaire). */
  rangeRefs: ({ s: string; e: string } | null)[];
}

const num = (v: CellValue | undefined): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'string') {
    const n = Number(v.replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const str = (v: CellValue | undefined): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'boolean') return v ? 'VRAI' : 'FAUX';
  return String(v);
};

const nums = (list: CellValue[]): number[] =>
  list
    .filter((v) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(String(v).replace(',', '.'))))
    .map((v) => Number(String(v).replace(',', '.')));

function excelDateToJs(serial: number): Date {
  // Excel : jour 1 = 1900-01-01 (avec le bug du 29/02/1900)
  return new Date(Date.UTC(1899, 11, 30 + Math.floor(serial)));
}

function jsToExcelDate(d: Date): number {
  return Math.floor((d.getTime() - Date.UTC(1899, 11, 30)) / 86400000);
}

function toDate(v: CellValue): Date | null {
  if (typeof v === 'number' && v > 0) return excelDateToJs(v);
  if (typeof v === 'string' && v.trim()) {
    const parsed = new Date(v);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
}

/** Grille 2D reconstruite à partir d'une plage plate. */
function toGrid(flat: CellValue[], ref: { s: string; e: string } | null): CellValue[][] | null {
  if (!ref) return null;
  const a = parseCellRef(ref.s);
  const b = parseCellRef(ref.e);
  if (!a || !b) return null;
  const cols = Math.abs(b.c - a.c) + 1;
  const rows = Math.abs(b.r - a.r) + 1;
  if (cols * rows > flat.length) return null;
  const grid: CellValue[][] = [];
  for (let r = 0; r < rows; r += 1) {
    grid.push(flat.slice(r * cols, (r + 1) * cols));
  }
  return grid;
}

function wildcardToRegex(pattern: string): RegExp {
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const withWildcards = escaped.replace(/\\\*/g, '.*').replace(/\\\?/g, '.');
  return new RegExp(`^${withWildcards}$`, 'i');
}

/** Transforme un critère Excel (« >10 », « <>x », « a* »…) en prédicat. */
export function criteriaMatcher(criteria: CellValue): (v: CellValue) => boolean {
  const raw = typeof criteria === 'string' ? criteria.trim() : criteria;
  if (typeof raw === 'number') return (v) => num(v) === raw;
  const text = String(raw ?? '');
  const opMatch = /^(<=|>=|<>|=|<|>)(.*)$/.exec(text);
  const op = opMatch ? opMatch[1] : '=';
  const operandRaw = opMatch ? opMatch[2].trim() : text;
  const operandNum = Number(operandRaw.replace(',', '.'));
  const numeric = operandRaw !== '' && Number.isFinite(operandNum);
  const regex = operandRaw.includes('*') || operandRaw.includes('?') ? wildcardToRegex(operandRaw) : null;
  return (v) => {
    const textValue = str(v);
    if (numeric) {
      const n = num(v);
      switch (op) {
        case '>':
          return n > operandNum;
        case '<':
          return n < operandNum;
        case '>=':
          return n >= operandNum;
        case '<=':
          return n <= operandNum;
        case '<>':
          return n !== operandNum;
        default:
          return n === operandNum;
      }
    }
    const equals = regex ? regex.test(textValue) : textValue.toLowerCase() === operandRaw.toLowerCase();
    if (op === '<>') return !equals;
    if (op === '=') return equals;
    const cmp = textValue.localeCompare(operandRaw, 'fr');
    if (op === '>') return cmp > 0;
    if (op === '<') return cmp < 0;
    if (op === '>=') return cmp >= 0;
    if (op === '<=') return cmp <= 0;
    return equals;
  };
}

function countIfsJoin(a: FnArgs): number {
  const target = a.ranges[0] || [];
  const pairs: { test: (v: CellValue) => boolean; values: CellValue[] }[] = [];
  for (let i = 0; i + 1 < a.args.length; i += 2) {
    pairs.push({ test: criteriaMatcher(a.args[i + 1]), values: a.ranges[i] || target });
  }
  return target.filter((_, index) => pairs.every((p) => p.test(p.values[index] ?? null))).length;
}

/** SOMME.SI.ENS(somme; plage_crit; critère; …) */
function sumIfsJoin(a: FnArgs): number {
  const sumRange = a.ranges[0] || [];
  const pairs: { test: (v: CellValue) => boolean; values: CellValue[] }[] = [];
  for (let i = 1; i + 1 < a.args.length; i += 2) {
    pairs.push({ test: criteriaMatcher(a.args[i + 1]), values: a.ranges[i] || sumRange });
  }
  let total = 0;
  sumRange.forEach((v, index) => {
    if (pairs.every((p) => p.test(p.values[index] ?? null))) total += num(v);
  });
  return total;
}

function textJoin(parts: CellValue[], delimiter: string, ignoreEmpty: boolean): string {
  return parts
    .map((p) => str(p))
    .filter((p) => (ignoreEmpty ? p !== '' : true))
    .join(delimiter);
}

const DATE_TOKEN = /^(yyyy|yy|mmmm|mmm|mm|m|dddd|ddd|dd|d|hh|h|ss|s)$/i;

function formatDateValue(value: CellValue, pattern: string): string {
  const date = toDate(value);
  if (!date) return str(value);
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  return pattern.replace(/"[^"]*"|(yyyy|yy|mmmm|mmm|mm|m|dddd|ddd|dd|d|hh|h|ss|s)/gi, (match) => {
    const token = match.toLowerCase();
    if (!DATE_TOKEN.test(token)) return match.replace(/"/g, '');
    switch (token) {
      case 'yyyy':
        return String(date.getFullYear());
      case 'yy':
        return pad(date.getFullYear() % 100);
      case 'mmmm':
        return months[date.getMonth()];
      case 'mmm':
        return months[date.getMonth()].slice(0, 3);
      case 'mm':
        return pad(date.getMonth() + 1);
      case 'm':
        return String(date.getMonth() + 1);
      case 'dddd':
        return date.toLocaleDateString('fr-FR', { weekday: 'long' });
      case 'ddd':
        return date.toLocaleDateString('fr-FR', { weekday: 'short' });
      case 'dd':
        return pad(date.getDate());
      case 'd':
        return String(date.getDate());
      case 'hh':
        return pad(date.getHours());
      case 'h':
        return String(date.getHours());
      case 'ss':
      case 's':
        return pad(date.getSeconds());
      default:
        return match;
    }
  });
}

function pmt(rate: number, nper: number, pv: number, fv = 0, type = 0): number {
  if (rate === 0) return -(pv + fv) / nper;
  const pow = Math.pow(1 + rate, nper);
  return (-rate * (pv * pow + fv)) / ((pow - 1) * (type ? 1 + rate : 1));
}

function npv(rate: number, values: number[]): number {
  return values.reduce((acc, v, i) => acc + v / Math.pow(1 + rate, i + 1), 0);
}

function irr(values: number[], guess = 0.1): number {
  let rate = guess;
  for (let i = 0; i < 60; i += 1) {
    const f = values.reduce((acc, v, idx) => acc + v / Math.pow(1 + rate, idx), 0);
    const d = values.reduce((acc, v, idx) => acc - (idx * v) / Math.pow(1 + rate, idx + 1), 0);
    if (Math.abs(d) < 1e-10) break;
    const next = rate - f / d;
    if (!Number.isFinite(next)) break;
    if (Math.abs(next - rate) < 1e-9) {
      rate = next;
      break;
    }
    rate = next;
  }
  return rate;
}

/** Retourne undefined si la fonction n'est pas gérée. */
export function callExcelFunction(name: string, a: FnArgs): CellValue | undefined {
  const v1 = a.args[0];
  const v2 = a.args[1];
  const v3 = a.args[2];
  const n1 = num(v1);
  const n2 = num(v2);
  const numbers = nums(a.values);

  switch (name) {
    // ── Mathématiques ─────────────────────────────────────────────
    case 'ABS':
      return Math.abs(n1);
    case 'ARRONDI':
    case 'ROUND': {
      const f = Math.pow(10, Math.round(n2));
      return Math.round(n1 * f) / f;
    }
    case 'ARRONDI.SUP':
    case 'ROUNDUP': {
      const f = Math.pow(10, Math.round(n2));
      return Math.ceil(n1 * f) / f;
    }
    case 'ARRONDI.INF':
    case 'ROUNDDOWN': {
      const f = Math.pow(10, Math.round(n2));
      return Math.floor(n1 * f) / f;
    }
    case 'ENT':
    case 'INT':
      return Math.floor(n1);
    case 'TRONQUE':
    case 'TRUNC': {
      const f = Math.pow(10, Math.round(n2));
      return Math.trunc(n1 * f) / f;
    }
    case 'MOD':
      return n2 === 0 ? NaN : n1 - n2 * Math.floor(n1 / n2);
    case 'SIGNE':
    case 'SIGN':
      return Math.sign(n1);
    case 'PUISSANCE':
    case 'POWER':
      return Math.pow(n1, n2);
    case 'RACINE':
    case 'SQRT':
      return Math.sqrt(n1);
    case 'EXP':
      return Math.exp(n1);
    case 'LN':
      return Math.log(n1);
    case 'LOG':
      return Math.log(n1) / Math.log(n2 || 10);
    case 'LOG10':
      return Math.log10(n1);
    case 'PI':
      return Math.PI;
    case 'ALEA':
    case 'RAND':
      return Math.random();
    case 'ALEAENTRE.BORNES':
    case 'ALEA.ENTRE.BORNES':
    case 'RANDBETWEEN':
      return Math.floor(Math.random() * (n2 - n1 + 1)) + n1;
    case 'SOMME':
    case 'SUM':
      return numbers.reduce((x, y) => x + y, 0);
    case 'PRODUIT':
    case 'PRODUCT':
      return numbers.reduce((x, y) => x * y, 1);
    case 'SOMMEPROD':
    case 'SUMPRODUCT': {
      const lists = a.ranges.filter((r) => r.length > 0);
      if (!lists.length) return 0;
      const len = Math.min(...lists.map((l) => l.length));
      let total = 0;
      for (let i = 0; i < len; i += 1) {
        total += lists.reduce((acc, l) => acc * num(l[i]), 1);
      }
      return total;
    }
    case 'SOMME.CARRES':
    case 'SUMSQ':
      return numbers.reduce((x, y) => x + y * y, 0);
    case 'SIN':
      return Math.sin(n1);
    case 'COS':
      return Math.cos(n1);
    case 'TAN':
      return Math.tan(n1);
    case 'ASIN':
      return Math.asin(n1);
    case 'ACOS':
      return Math.acos(n1);
    case 'ATAN':
      return Math.atan(n1);
    case 'SINH':
      return Math.sinh(n1);
    case 'COSH':
      return Math.cosh(n1);
    case 'TANH':
      return Math.tanh(n1);
    case 'DEGRES':
    case 'DEGREES':
      return (n1 * 180) / Math.PI;
    case 'RADIANS':
      return (n1 * Math.PI) / 180;
    case 'PGCD':
    case 'GCD': {
      const gcd = (x: number, y: number): number => (y === 0 ? Math.abs(x) : gcd(y, x % y));
      return numbers.reduce((x, y) => gcd(x, y), 0);
    }
    case 'PPCM':
    case 'LCM': {
      const gcd = (x: number, y: number): number => (y === 0 ? Math.abs(x) : gcd(y, x % y));
      return numbers.reduce((x, y) => (x && y ? Math.abs(x * y) / gcd(x, y) : 0), 1);
    }
    case 'FACT':
      return n1 <= 1 ? 1 : Array.from({ length: Math.floor(n1) }, (_, i) => i + 1).reduce((x, y) => x * y, 1);
    case 'COMBIN':
      return n1 < n2 ? 0 : (() => {
        let result = 1;
        for (let i = 1; i <= n2; i += 1) result = (result * (n1 - n2 + i)) / i;
        return Math.round(result);
      })();
    case 'ARRANG':
      return n1 < n2 ? 0 : Array.from({ length: n2 }, (_, i) => n1 - i).reduce((x, y) => x * y, 1);
    case 'QUOTIENT':
      return n2 === 0 ? NaN : Math.trunc(n1 / n2);
    case 'PAIR':
    case 'ISEVEN':
      return Math.round(n1) % 2 === 0;
    case 'IMPAIR':
    case 'ISODD':
      return Math.abs(Math.round(n1) % 2) === 1;
    case 'PLAFOND':
    case 'CEILING':
      return Math.ceil(n1 / (n2 || 1)) * (n2 || 1);
    case 'PLANCHER':
    case 'FLOOR':
      return Math.floor(n1 / (n2 || 1)) * (n2 || 1);

    // ── Statistiques ──────────────────────────────────────────────
    case 'MAX':
      return numbers.length ? Math.max(...numbers) : 0;
    case 'MIN':
      return numbers.length ? Math.min(...numbers) : 0;
    case 'MAXA':
      return numbers.length ? Math.max(...numbers, ...a.values.map(num)) : 0;
    case 'MINA':
      return numbers.length ? Math.min(...numbers) : 0;
    case 'MOYENNE':
    case 'AVERAGE':
      return numbers.length ? numbers.reduce((x, y) => x + y, 0) / numbers.length : 0;
    case 'MOYENNE.GEOMETRIQUE':
    case 'GEOMEAN':
      return numbers.length ? Math.pow(numbers.reduce((x, y) => x * y, 1), 1 / numbers.length) : 0;
    case 'MOYENNE.HARMONIQUE':
    case 'HARMEAN':
      return numbers.length ? numbers.length / numbers.reduce((x, y) => x + 1 / y, 0) : 0;
    case 'MEDIANE':
    case 'MEDIAN': {
      if (!numbers.length) return 0;
      const sorted = [...numbers].sort((x, y) => x - y);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    }
    case 'MODE':
    case 'MODE.SIMPLE': {
      const counts = new Map<number, number>();
      numbers.forEach((n) => counts.set(n, (counts.get(n) || 0) + 1));
      let best: number | null = null;
      let bestCount = 0;
      counts.forEach((count, value) => {
        if (count > bestCount) {
          best = value;
          bestCount = count;
        }
      });
      return best ?? 0;
    }
    case 'NB':
    case 'COUNT':
      return numbers.length;
    case 'NBVAL':
    case 'COUNTA':
      return a.values.filter((v) => v !== null && v !== undefined && str(v) !== '').length;
    case 'NB.VIDE':
    case 'COUNTBLANK':
      return a.values.filter((v) => v === null || v === undefined || str(v) === '').length;
    case 'ECARTYPE':
    case 'ECARTYPE.STANDARD':
    case 'STDEV':
    case 'STDEV.S': {
      if (numbers.length < 2) return 0;
      const mean = numbers.reduce((x, y) => x + y, 0) / numbers.length;
      return Math.sqrt(numbers.reduce((acc, n) => acc + (n - mean) ** 2, 0) / (numbers.length - 1));
    }
    case 'ECARTYPE.P':
    case 'STDEV.P':
    case 'ECARTYPEP': {
      if (!numbers.length) return 0;
      const mean = numbers.reduce((x, y) => x + y, 0) / numbers.length;
      return Math.sqrt(numbers.reduce((acc, n) => acc + (n - mean) ** 2, 0) / numbers.length);
    }
    case 'VAR':
    case 'VAR.S': {
      if (numbers.length < 2) return 0;
      const mean = numbers.reduce((x, y) => x + y, 0) / numbers.length;
      return numbers.reduce((acc, n) => acc + (n - mean) ** 2, 0) / (numbers.length - 1);
    }
    case 'VAR.P':
    case 'VARP': {
      if (!numbers.length) return 0;
      const mean = numbers.reduce((x, y) => x + y, 0) / numbers.length;
      return numbers.reduce((acc, n) => acc + (n - mean) ** 2, 0) / numbers.length;
    }
    case 'GRANDE.VALEUR':
    case 'LARGE': {
      const sorted = [...numbers].sort((x, y) => y - x);
      return sorted[Math.max(0, Math.round(n2) - 1)] ?? 0;
    }
    case 'PETITE.VALEUR':
    case 'SMALL': {
      const sorted = [...numbers].sort((x, y) => x - y);
      return sorted[Math.max(0, Math.round(n2) - 1)] ?? 0;
    }
    case 'RANG':
    case 'RANK': {
      const sorted = [...numbers].sort((x, y) => y - x);
      return sorted.indexOf(n1) + 1;
    }
    case 'CENTILE':
    case 'PERCENTILE': {
      if (!numbers.length) return 0;
      const sorted = [...numbers].sort((x, y) => x - y);
      const pos = (sorted.length - 1) * (n2 / 100);
      const low = Math.floor(pos);
      const high = Math.ceil(pos);
      return sorted[low] + (sorted[high] - sorted[low]) * (pos - low);
    }
    case 'QUARTILE': {
      const sorted = [...numbers].sort((x, y) => x - y);
      if (!sorted.length) return 0;
      const q = Math.max(0, Math.min(4, Math.round(n2)));
      return sorted[Math.round(((sorted.length - 1) * q) / 4)] ?? 0;
    }
    case 'NB.SI':
    case 'COUNTIF': {
      const test = criteriaMatcher(v2);
      return (a.ranges[0] || []).filter((v) => test(v)).length;
    }
    case 'NB.SI.ENS':
    case 'COUNTIFS':
      return countIfsJoin(a);
    case 'SOMME.SI':
    case 'SUMIF': {
      const test = criteriaMatcher(v2);
      const range = a.ranges[0] || [];
      const sumRange = a.ranges[2] && a.ranges[2].length ? a.ranges[2] : range;
      return range.reduce((acc, v, i) => (test(v) ? acc + num(sumRange[i] ?? v) : acc), 0);
    }
    case 'SOMME.SI.ENS':
    case 'SUMIFS':
      return sumIfsJoin(a);
    case 'MOYENNE.SI':
    case 'AVERAGEIF': {
      const test = criteriaMatcher(v2);
      const range = a.ranges[0] || [];
      const avgRange = a.ranges[2] && a.ranges[2].length ? a.ranges[2] : range;
      const picked = range.filter((v) => test(v)).map((_, i) => num(avgRange[i]));
      return picked.length ? picked.reduce((x, y) => x + y, 0) / picked.length : 0;
    }
    case 'MAXIFS':
    case 'MINIFS': {
      const range = a.ranges[0] || [];
      const test = criteriaMatcher(v2);
      const compareRange = a.ranges[1] || range;
      const picked = range.filter((_, i) => test(compareRange[i] ?? null)).map(num);
      if (!picked.length) return 0;
      return name === 'MAXIFS' ? Math.max(...picked) : Math.min(...picked);
    }

    // ── Logique ───────────────────────────────────────────────────
    case 'ET':
    case 'AND':
      return a.args.every((v) => {
        const s = str(v).toLowerCase();
        return v === true || num(v) !== 0 || s === 'vrai';
      });
    case 'OU':
    case 'OR':
      return a.args.some((v) => {
        const s = str(v).toLowerCase();
        return v === true || num(v) !== 0 || s === 'vrai';
      });
    case 'NON':
    case 'NOT':
      return !(v1 === true || num(v1) !== 0 || str(v1).toLowerCase() === 'vrai');
    case 'OU.EXCLUSIF':
    case 'XOR':
      return a.args.filter((v) => num(v) !== 0).length % 2 === 1;
    case 'SIERREUR':
    case 'IFERROR':
      return a.args[0] === null || a.args[0] === undefined || ['#NOM?', '#REF!', '#ERROR!', '#DIV/0!'].includes(str(a.args[0]))
        ? v2
        : a.args[0];
    case 'ESTVIDE':
    case 'ISBLANK':
      return v1 === null || v1 === undefined || str(v1) === '';
    case 'ESTNUM':
    case 'ISNUMBER':
      return typeof v1 === 'number' || (str(v1) !== '' && !Number.isNaN(Number(str(v1).replace(',', '.'))));
    case 'ESTTEXTE':
    case 'ISTEXT':
      return typeof v1 === 'string' && str(v1) !== '' && Number.isNaN(Number(str(v1).replace(',', '.')));
    case 'ESTERREUR':
    case 'ISERROR':
      return ['#NOM?', '#REF!', '#ERROR!', '#DIV/0!'].includes(str(v1));
    case 'ESTLOGIQUE':
    case 'ISLOGICAL':
      return typeof v1 === 'boolean' || ['vrai', 'faux', 'true', 'false'].includes(str(v1).toLowerCase());

    // ── Texte ────────────────────────────────────────────────────
    case 'MAJUSCULE':
    case 'UPPER':
      return str(v1).toUpperCase();
    case 'MINUSCULE':
    case 'LOWER':
      return str(v1).toLowerCase();
    case 'NOMPROPRE':
    case 'PROPER':
      return str(v1).toLowerCase().replace(/(^|[\s'-])(\S)/g, (_, p, c) => p + c.toUpperCase());
    case 'GAUCHE':
    case 'LEFT':
      return str(v1).slice(0, v2 === undefined ? 1 : Math.max(0, Math.round(n2)));
    case 'DROITE':
    case 'RIGHT': {
      const count = v2 === undefined ? 1 : Math.max(0, Math.round(n2));
      return count === 0 ? '' : str(v1).slice(-count);
    }
    case 'STXT':
    case 'MID': {
      const start = Math.max(1, Math.round(n2));
      const len = v3 === undefined ? str(v1).length : Math.max(0, Math.round(num(v3)));
      return str(v1).slice(start - 1, start - 1 + len);
    }
    case 'NBCAR':
    case 'LEN':
      return str(v1).length;
    case 'SUPPRESPACE':
    case 'TRIM':
      return str(v1).replace(/\s+/g, ' ').trim();
    case 'SUBSTITUE':
    case 'SUBSTITUTE':
      return str(v1).split(str(v2)).join(str(v3));
    case 'REMPLACE':
    case 'REPLACE': {
      const start = Math.max(1, Math.round(n2));
      const len = Math.max(0, Math.round(num(v3)));
      const text = str(v1);
      return text.slice(0, start - 1) + str(a.args[3]) + text.slice(start - 1 + len);
    }
    case 'CHERCHE':
    case 'SEARCH': {
      const index = str(v1).toLowerCase().indexOf(str(v2).toLowerCase());
      return index < 0 ? '#VALEUR!' : index + 1;
    }
    case 'TROUVE':
    case 'FIND': {
      const index = str(v1).indexOf(str(v2));
      return index < 0 ? '#VALEUR!' : index + 1;
    }
    case 'EXACT':
      return str(v1) === str(v2);
    case 'REPT':
      return str(v1).repeat(Math.max(0, Math.round(n2)));
    case 'JOINDRE.TEXTE':
    case 'TEXTJOIN': {
      const delimiter = str(v1);
      const ignoreEmpty = v2 === true || str(v2).toLowerCase() === 'vrai';
      const parts = a.ranges.slice(2).flat();
      return textJoin(parts.length ? parts : a.args.slice(2), delimiter, ignoreEmpty);
    }
    case 'CONCATENER':
    case 'CONCAT':
    case 'CONCATENATE':
      return a.values.map(str).join('');
    case 'TEXTE':
    case 'TEXT':
      return formatDateValue(v1, str(v2));
    case 'CNUM':
    case 'VALUE':
      return num(v1);
    case 'CAR':
    case 'CHAR':
      return String.fromCharCode(Math.round(n1));
    case 'CODE':
    case 'UNICODE':
      return str(v1).charCodeAt(0) || 0;
    case 'UNICAR':
    case 'UNICHAR':
      return String.fromCodePoint(Math.round(n1));
    case 'TEXTE.AVANT':
    case 'TEXTBEFORE': {
      const index = str(v1).indexOf(str(v2));
      return index < 0 ? str(v1) : str(v1).slice(0, index);
    }
    case 'TEXTE.APRES':
    case 'TEXTAFTER': {
      const index = str(v1).indexOf(str(v2));
      return index < 0 ? '' : str(v1).slice(index + str(v2).length);
    }

    // ── Dates et heures ──────────────────────────────────────────
    case 'AUJOURDHUI':
    case 'TODAY': {
      const now = new Date();
      return jsToExcelDate(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
    }
    case 'MAINTENANT':
    case 'NOW': {
      const now = new Date();
      return jsToExcelDate(now) + (now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()) / 86400;
    }
    case 'ANNEE':
    case 'YEAR': {
      const d = toDate(v1);
      return d ? d.getFullYear() : '#VALEUR!';
    }
    case 'MOIS':
    case 'MONTH': {
      const d = toDate(v1);
      return d ? d.getMonth() + 1 : '#VALEUR!';
    }
    case 'JOUR':
    case 'DAY': {
      const d = toDate(v1);
      return d ? d.getDate() : '#VALEUR!';
    }
    case 'HEURE':
    case 'HOUR': {
      const d = toDate(v1);
      return d ? d.getHours() : '#VALEUR!';
    }
    case 'MINUTE': {
      const d = toDate(v1);
      return d ? d.getMinutes() : '#VALEUR!';
    }
    case 'SECONDE':
    case 'SECOND': {
      const d = toDate(v1);
      return d ? d.getSeconds() : '#VALEUR!';
    }
    case 'DATE':
      return jsToExcelDate(new Date(Date.UTC(Math.round(n1), Math.round(n2) - 1, Math.round(num(v3)))));
    case 'DATEVAL':
    case 'DATEVALUE': {
      const d = toDate(v1);
      return d ? jsToExcelDate(d) : '#VALEUR!';
    }
    case 'JOURSEM':
    case 'WEEKDAY': {
      const d = toDate(v1);
      return d ? d.getDay() + 1 : '#VALEUR!';
    }
    case 'NO.SEMAINE':
    case 'WEEKNUM': {
      const d = toDate(v1);
      if (!d) return '#VALEUR!';
      const start = new Date(d.getFullYear(), 0, 1);
      return Math.ceil(((d.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7);
    }
    case 'FIN.MOIS':
    case 'EOMONTH': {
      const d = toDate(v1);
      if (!d) return '#VALEUR!';
      return jsToExcelDate(new Date(Date.UTC(d.getFullYear(), d.getMonth() + Math.round(n2) + 1, 0)));
    }
    case 'MOIS.DECALER':
    case 'EDATE': {
      const d = toDate(v1);
      if (!d) return '#VALEUR!';
      return jsToExcelDate(new Date(Date.UTC(d.getFullYear(), d.getMonth() + Math.round(n2), d.getDate())));
    }
    case 'DATEDIF': {
      const start = toDate(v1);
      const end = toDate(v2);
      if (!start || !end) return '#VALEUR!';
      const unit = str(v3).toUpperCase() || 'D';
      const diffDays = Math.round((end.getTime() - start.getTime()) / 86400000);
      if (unit.startsWith('Y')) return end.getFullYear() - start.getFullYear();
      if (unit.startsWith('M')) {
        return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      }
      return diffDays;
    }
    case 'NB.JOURS.OUVRES':
    case 'NETWORKDAYS': {
      const start = toDate(v1);
      const end = toDate(v2);
      if (!start || !end) return '#VALEUR!';
      let count = 0;
      const cursor = new Date(start.getTime());
      while (cursor <= end) {
        const day = cursor.getDay();
        if (day !== 0 && day !== 6) count += 1;
        cursor.setDate(cursor.getDate() + 1);
      }
      return count;
    }
    case 'SERIE.JOUR.OUVRE':
    case 'WORKDAY': {
      const start = toDate(v1);
      if (!start) return '#VALEUR!';
      const cursor = new Date(start.getTime());
      let remaining = Math.round(n2);
      const step = remaining >= 0 ? 1 : -1;
      remaining = Math.abs(remaining);
      while (remaining > 0) {
        cursor.setDate(cursor.getDate() + step);
        const day = cursor.getDay();
        if (day !== 0 && day !== 6) remaining -= 1;
      }
      return jsToExcelDate(cursor);
    }

    // ── Recherche ────────────────────────────────────────────────
    case 'RECHERCHEV':
    case 'VLOOKUP': {
      const grid = toGrid(a.ranges[1] || [], a.rangeRefs[1] || null);
      if (!grid) return '#VALEUR!';
      const colIndex = Math.max(1, Math.round(num(v3))) - 1;
      const exact = a.args[3] === undefined ? true : !(num(a.args[3]) === 0);
      const needle = str(v1).toLowerCase();
      for (const row of grid) {
        const candidate = str(row[0]).toLowerCase();
        const match = exact
          ? candidate === needle
          : candidate.startsWith(needle.slice(0, Math.max(1, needle.length)));
        if (match) return row[colIndex] ?? '';
      }
      return '#N/A';
    }
    case 'RECHERCHEH':
    case 'HLOOKUP': {
      const grid = toGrid(a.ranges[1] || [], a.rangeRefs[1] || null);
      if (!grid || !grid.length) return '#VALEUR!';
      const rowIndex = Math.max(1, Math.round(num(v3))) - 1;
      const needle = str(v1).toLowerCase();
      const col = grid[0].findIndex((cell) => str(cell).toLowerCase() === needle);
      if (col < 0) return '#N/A';
      return grid[rowIndex]?.[col] ?? '';
    }
    case 'INDEX': {
      const grid = toGrid(a.ranges[0] || [], a.rangeRefs[0] || null);
      if (!grid) return '#VALEUR!';
      const row = Math.round(n2) - 1;
      const col = v3 === undefined ? 0 : Math.round(num(v3)) - 1;
      return grid[row]?.[col] ?? '#REF!';
    }
    case 'EQUIV':
    case 'MATCH': {
      const list = a.ranges[1] || [];
      const needle = str(v1).toLowerCase();
      const index = list.findIndex((cell) => str(cell).toLowerCase() === needle);
      return index < 0 ? '#N/A' : index + 1;
    }
    case 'CHOISIR':
    case 'CHOOSE': {
      const index = Math.round(n1);
      return a.args[index] ?? '#VALEUR!';
    }

    // ── Financier ────────────────────────────────────────────────
    case 'VAN':
    case 'NPV':
      return npv(n1, nums(a.ranges[1] || a.values.slice(1)));
    case 'TRI':
    case 'IRR':
      return irr(nums(a.ranges[0] || a.values));
    case 'VPM':
    case 'PMT':
      return pmt(num(v1), num(v2), num(v3), num(a.args[3]), num(a.args[4]));
    case 'VC':
    case 'FV': {
      const rate = num(v1);
      const nper = num(v2);
      const payment = num(v3);
      const pv = num(a.args[3]);
      return -(pv * Math.pow(1 + rate, nper) + payment * ((Math.pow(1 + rate, nper) - 1) / (rate || 1)));
    }
    case 'VA':
    case 'PV': {
      const rate = num(v1);
      const nper = num(v2);
      const payment = num(v3);
      return -(payment * ((1 - Math.pow(1 + rate, -nper)) / (rate || 1)));
    }
    case 'NB.VPM':
    case 'NPER': {
      const rate = num(v1);
      const payment = num(v2);
      const pv = num(v3);
      if (rate === 0) return -pv / payment;
      return Math.log((payment - pv * rate) / payment) / Math.log(1 + rate);
    }
    case 'AMORLIN':
    case 'SLN':
      return (num(v1) - num(v2)) / (num(v3) || 1);

    default:
      return undefined;
  }
}

export interface FunctionInfo {
  name: string;
  label: string;
  syntax: string;
  category: string;
}

/** Catalogue affiché dans le navigateur de fonctions. */
export const FUNCTION_CATALOG: FunctionInfo[] = [
  { name: 'SOMME', label: 'Additionne des nombres', syntax: '=SOMME(plage)', category: 'Mathématiques' },
  { name: 'PRODUIT', label: 'Multiplie des nombres', syntax: '=PRODUIT(plage)', category: 'Mathématiques' },
  { name: 'SOMMEPROD', label: 'Somme des produits', syntax: '=SOMMEPROD(plage1;plage2)', category: 'Mathématiques' },
  { name: 'SOMME.CARRES', label: 'Somme des carrés', syntax: '=SOMME.CARRES(plage)', category: 'Mathématiques' },
  { name: 'ABS', label: 'Valeur absolue', syntax: '=ABS(nombre)', category: 'Mathématiques' },
  { name: 'ARRONDI', label: 'Arrondit un nombre', syntax: '=ARRONDI(nombre;décimales)', category: 'Mathématiques' },
  { name: 'ARRONDI.SUP', label: 'Arrondit au supérieur', syntax: '=ARRONDI.SUP(nombre;décimales)', category: 'Mathématiques' },
  { name: 'ARRONDI.INF', label: 'Arrondit à l’inférieur', syntax: '=ARRONDI.INF(nombre;décimales)', category: 'Mathématiques' },
  { name: 'ENT', label: 'Partie entière', syntax: '=ENT(nombre)', category: 'Mathématiques' },
  { name: 'TRONQUE', label: 'Tronque un nombre', syntax: '=TRONQUE(nombre;décimales)', category: 'Mathématiques' },
  { name: 'MOD', label: 'Reste de la division', syntax: '=MOD(nombre;diviseur)', category: 'Mathématiques' },
  { name: 'SIGNE', label: 'Signe du nombre', syntax: '=SIGNE(nombre)', category: 'Mathématiques' },
  { name: 'PUISSANCE', label: 'Puissance', syntax: '=PUISSANCE(nombre;exposant)', category: 'Mathématiques' },
  { name: 'RACINE', label: 'Racine carrée', syntax: '=RACINE(nombre)', category: 'Mathématiques' },
  { name: 'EXP', label: 'Exponentielle', syntax: '=EXP(nombre)', category: 'Mathématiques' },
  { name: 'LN', label: 'Logarithme népérien', syntax: '=LN(nombre)', category: 'Mathématiques' },
  { name: 'LOG', label: 'Logarithme de base n', syntax: '=LOG(nombre;base)', category: 'Mathématiques' },
  { name: 'LOG10', label: 'Logarithme décimal', syntax: '=LOG10(nombre)', category: 'Mathématiques' },
  { name: 'PI', label: 'Constante Pi', syntax: '=PI()', category: 'Mathématiques' },
  { name: 'ALEA', label: 'Nombre aléatoire', syntax: '=ALEA()', category: 'Mathématiques' },
  { name: 'ALEAENTRE.BORNES', label: 'Entier aléatoire entre deux bornes', syntax: '=ALEAENTRE.BORNES(1;100)', category: 'Mathématiques' },
  { name: 'SIN', label: 'Sinus', syntax: '=SIN(angle)', category: 'Mathématiques' },
  { name: 'COS', label: 'Cosinus', syntax: '=COS(angle)', category: 'Mathématiques' },
  { name: 'TAN', label: 'Tangente', syntax: '=TAN(angle)', category: 'Mathématiques' },
  { name: 'ASIN', label: 'Arc sinus', syntax: '=ASIN(nombre)', category: 'Mathématiques' },
  { name: 'ACOS', label: 'Arc cosinus', syntax: '=ACOS(nombre)', category: 'Mathématiques' },
  { name: 'ATAN', label: 'Arc tangente', syntax: '=ATAN(nombre)', category: 'Mathématiques' },
  { name: 'DEGRES', label: 'Radians en degrés', syntax: '=DEGRES(radians)', category: 'Mathématiques' },
  { name: 'RADIANS', label: 'Degrés en radians', syntax: '=RADIANS(degrés)', category: 'Mathématiques' },
  { name: 'PGCD', label: 'Plus grand commun diviseur', syntax: '=PGCD(nombre1;nombre2)', category: 'Mathématiques' },
  { name: 'PPCM', label: 'Plus petit commun multiple', syntax: '=PPCM(nombre1;nombre2)', category: 'Mathématiques' },
  { name: 'FACT', label: 'Factorielle', syntax: '=FACT(nombre)', category: 'Mathématiques' },
  { name: 'COMBIN', label: 'Combinaisons', syntax: '=COMBIN(n;k)', category: 'Mathématiques' },
  { name: 'ARRANG', label: 'Arrangements', syntax: '=ARRANG(n;k)', category: 'Mathématiques' },
  { name: 'QUOTIENT', label: 'Quotient entier', syntax: '=QUOTIENT(nombre;diviseur)', category: 'Mathématiques' },
  { name: 'PLAFOND', label: 'Arrondit au multiple supérieur', syntax: '=PLAFOND(nombre;multiple)', category: 'Mathématiques' },
  { name: 'PLANCHER', label: 'Arrondit au multiple inférieur', syntax: '=PLANCHER(nombre;multiple)', category: 'Mathématiques' },

  { name: 'MOYENNE', label: 'Moyenne arithmétique', syntax: '=MOYENNE(plage)', category: 'Statistiques' },
  { name: 'MEDIANE', label: 'Valeur médiane', syntax: '=MEDIANE(plage)', category: 'Statistiques' },
  { name: 'MODE', label: 'Valeur la plus fréquente', syntax: '=MODE(plage)', category: 'Statistiques' },
  { name: 'MAX', label: 'Valeur maximale', syntax: '=MAX(plage)', category: 'Statistiques' },
  { name: 'MIN', label: 'Valeur minimale', syntax: '=MIN(plage)', category: 'Statistiques' },
  { name: 'NB', label: 'Compte les nombres', syntax: '=NB(plage)', category: 'Statistiques' },
  { name: 'NBVAL', label: 'Compte les valeurs non vides', syntax: '=NBVAL(plage)', category: 'Statistiques' },
  { name: 'NB.VIDE', label: 'Compte les cellules vides', syntax: '=NB.VIDE(plage)', category: 'Statistiques' },
  { name: 'ECARTYPE.STANDARD', label: 'Écart-type (échantillon)', syntax: '=ECARTYPE.STANDARD(plage)', category: 'Statistiques' },
  { name: 'ECARTYPE.P', label: 'Écart-type (population)', syntax: '=ECARTYPE.P(plage)', category: 'Statistiques' },
  { name: 'VAR.S', label: 'Variance (échantillon)', syntax: '=VAR.S(plage)', category: 'Statistiques' },
  { name: 'VAR.P', label: 'Variance (population)', syntax: '=VAR.P(plage)', category: 'Statistiques' },
  { name: 'GRANDE.VALEUR', label: 'k-ième plus grande valeur', syntax: '=GRANDE.VALEUR(plage;2)', category: 'Statistiques' },
  { name: 'PETITE.VALEUR', label: 'k-ième plus petite valeur', syntax: '=PETITE.VALEUR(plage;2)', category: 'Statistiques' },
  { name: 'RANG', label: 'Rang d’une valeur', syntax: '=RANG(valeur;plage)', category: 'Statistiques' },
  { name: 'CENTILE', label: 'Centile', syntax: '=CENTILE(plage;90)', category: 'Statistiques' },
  { name: 'QUARTILE', label: 'Quartile', syntax: '=QUARTILE(plage;1)', category: 'Statistiques' },
  { name: 'NB.SI', label: 'Compte selon un critère', syntax: '=NB.SI(plage;critère)', category: 'Statistiques' },
  { name: 'NB.SI.ENS', label: 'Compte selon plusieurs critères', syntax: '=NB.SI.ENS(plage1;crit1;plage2;crit2)', category: 'Statistiques' },
  { name: 'SOMME.SI', label: 'Somme selon un critère', syntax: '=SOMME.SI(plage;critère;plage_somme)', category: 'Statistiques' },
  { name: 'SOMME.SI.ENS', label: 'Somme selon plusieurs critères', syntax: '=SOMME.SI.ENS(somme;plage;critère)', category: 'Statistiques' },
  { name: 'MOYENNE.SI', label: 'Moyenne selon un critère', syntax: '=MOYENNE.SI(plage;critère;plage_moy)', category: 'Statistiques' },
  { name: 'MAXIFS', label: 'Maximum selon critère', syntax: '=MAXIFS(plage;plage_crit;critère)', category: 'Statistiques' },
  { name: 'MINIFS', label: 'Minimum selon critère', syntax: '=MINIFS(plage;plage_crit;critère)', category: 'Statistiques' },

  { name: 'SI', label: 'Condition', syntax: '=SI(condition;vrai;faux)', category: 'Logique' },
  { name: 'SIERREUR', label: 'Valeur de repli en cas d’erreur', syntax: '=SIERREUR(valeur;repli)', category: 'Logique' },
  { name: 'ET', label: 'Et logique', syntax: '=ET(cond1;cond2)', category: 'Logique' },
  { name: 'OU', label: 'Ou logique', syntax: '=OU(cond1;cond2)', category: 'Logique' },
  { name: 'NON', label: 'Négation', syntax: '=NON(condition)', category: 'Logique' },
  { name: 'OU.EXCLUSIF', label: 'Ou exclusif', syntax: '=OU.EXCLUSIF(cond1;cond2)', category: 'Logique' },
  { name: 'ESTVIDE', label: 'Teste si la cellule est vide', syntax: '=ESTVIDE(cellule)', category: 'Logique' },
  { name: 'ESTNUM', label: 'Teste si c’est un nombre', syntax: '=ESTNUM(cellule)', category: 'Logique' },
  { name: 'ESTTEXTE', label: 'Teste si c’est du texte', syntax: '=ESTTEXTE(cellule)', category: 'Logique' },
  { name: 'ESTERREUR', label: 'Teste une erreur', syntax: '=ESTERREUR(cellule)', category: 'Logique' },

  { name: 'MAJUSCULE', label: 'Convertit en majuscules', syntax: '=MAJUSCULE(texte)', category: 'Texte' },
  { name: 'MINUSCULE', label: 'Convertit en minuscules', syntax: '=MINUSCULE(texte)', category: 'Texte' },
  { name: 'NOMPROPRE', label: 'Première lettre en majuscule', syntax: '=NOMPROPRE(texte)', category: 'Texte' },
  { name: 'GAUCHE', label: 'Extrait à gauche', syntax: '=GAUCHE(texte;n)', category: 'Texte' },
  { name: 'DROITE', label: 'Extrait à droite', syntax: '=DROITE(texte;n)', category: 'Texte' },
  { name: 'STXT', label: 'Extrait au milieu', syntax: '=STXT(texte;début;n)', category: 'Texte' },
  { name: 'NBCAR', label: 'Nombre de caractères', syntax: '=NBCAR(texte)', category: 'Texte' },
  { name: 'SUPPRESPACE', label: 'Supprime les espaces superflus', syntax: '=SUPPRESPACE(texte)', category: 'Texte' },
  { name: 'SUBSTITUE', label: 'Remplace un texte par un autre', syntax: '=SUBSTITUE(texte;ancien;nouveau)', category: 'Texte' },
  { name: 'REMPLACE', label: 'Remplace des caractères', syntax: '=REMPLACE(texte;début;n;nouveau)', category: 'Texte' },
  { name: 'CHERCHE', label: 'Position d’un texte (insensible à la casse)', syntax: '=CHERCHE(texte;dans)', category: 'Texte' },
  { name: 'TROUVE', label: 'Position d’un texte', syntax: '=TROUVE(texte;dans)', category: 'Texte' },
  { name: 'EXACT', label: 'Compare deux textes', syntax: '=EXACT(texte1;texte2)', category: 'Texte' },
  { name: 'REPT', label: 'Répète un texte', syntax: '=REPT(texte;n)', category: 'Texte' },
  { name: 'JOINDRE.TEXTE', label: 'Fusionne avec séparateur', syntax: '=JOINDRE.TEXTE("-";VRAI;plage)', category: 'Texte' },
  { name: 'CONCATENER', label: 'Concatène des textes', syntax: '=CONCATENER(texte1;texte2)', category: 'Texte' },
  { name: 'TEXTE', label: 'Formate une valeur', syntax: '=TEXTE(valeur;"jj/mm/aaaa")', category: 'Texte' },
  { name: 'TEXTE.AVANT', label: 'Texte avant un séparateur', syntax: '=TEXTE.AVANT(texte;"-")', category: 'Texte' },
  { name: 'TEXTE.APRES', label: 'Texte après un séparateur', syntax: '=TEXTE.APRES(texte;"-")', category: 'Texte' },
  { name: 'CAR', label: 'Caractère depuis un code', syntax: '=CAR(65)', category: 'Texte' },
  { name: 'CODE', label: 'Code d’un caractère', syntax: '=CODE("A")', category: 'Texte' },

  { name: 'AUJOURDHUI', label: 'Date du jour', syntax: '=AUJOURDHUI()', category: 'Date et heure' },
  { name: 'MAINTENANT', label: 'Date et heure actuelles', syntax: '=MAINTENANT()', category: 'Date et heure' },
  { name: 'ANNEE', label: 'Année d’une date', syntax: '=ANNEE(date)', category: 'Date et heure' },
  { name: 'MOIS', label: 'Mois d’une date', syntax: '=MOIS(date)', category: 'Date et heure' },
  { name: 'JOUR', label: 'Jour d’une date', syntax: '=JOUR(date)', category: 'Date et heure' },
  { name: 'HEURE', label: 'Heure', syntax: '=HEURE(heure)', category: 'Date et heure' },
  { name: 'MINUTE', label: 'Minutes', syntax: '=MINUTE(heure)', category: 'Date et heure' },
  { name: 'SECONDE', label: 'Secondes', syntax: '=SECONDE(heure)', category: 'Date et heure' },
  { name: 'DATE', label: 'Construit une date', syntax: '=DATE(2026;3;14)', category: 'Date et heure' },
  { name: 'DATEVAL', label: 'Convertit un texte en date', syntax: '=DATEVAL("2026-03-14")', category: 'Date et heure' },
  { name: 'JOURSEM', label: 'Jour de la semaine', syntax: '=JOURSEM(date)', category: 'Date et heure' },
  { name: 'NO.SEMAINE', label: 'Numéro de semaine', syntax: '=NO.SEMAINE(date)', category: 'Date et heure' },
  { name: 'FIN.MOIS', label: 'Dernier jour du mois', syntax: '=FIN.MOIS(date;0)', category: 'Date et heure' },
  { name: 'MOIS.DECALER', label: 'Décale la date de n mois', syntax: '=MOIS.DECALER(date;1)', category: 'Date et heure' },
  { name: 'DATEDIF', label: 'Écart entre deux dates', syntax: '=DATEDIF(date1;date2;"D")', category: 'Date et heure' },
  { name: 'NB.JOURS.OUVRES', label: 'Jours ouvrés entre deux dates', syntax: '=NB.JOURS.OUVRES(début;fin)', category: 'Date et heure' },
  { name: 'SERIE.JOUR.OUVRE', label: 'Ajoute des jours ouvrés', syntax: '=SERIE.JOUR.OUVRE(date;10)', category: 'Date et heure' },

  { name: 'RECHERCHEV', label: 'Recherche verticale', syntax: '=RECHERCHEV(valeur;plage;colonne;FAUX)', category: 'Recherche' },
  { name: 'RECHERCHEH', label: 'Recherche horizontale', syntax: '=RECHERCHEH(valeur;plage;ligne;FAUX)', category: 'Recherche' },
  { name: 'INDEX', label: 'Valeur par position', syntax: '=INDEX(plage;ligne;colonne)', category: 'Recherche' },
  { name: 'EQUIV', label: 'Position d’une valeur', syntax: '=EQUIV(valeur;plage;0)', category: 'Recherche' },
  { name: 'CHOISIR', label: 'Choisit parmi des valeurs', syntax: '=CHOISIR(2;"a";"b")', category: 'Recherche' },

  { name: 'VAN', label: 'Valeur actuelle nette', syntax: '=VAN(taux;plage)', category: 'Financier' },
  { name: 'TRI', label: 'Taux de rentabilité interne', syntax: '=TRI(plage)', category: 'Financier' },
  { name: 'VPM', label: 'Mensualité d’un emprunt', syntax: '=VPM(taux;nb;capital)', category: 'Financier' },
  { name: 'VC', label: 'Valeur future', syntax: '=VC(taux;nb;versement;capital)', category: 'Financier' },
  { name: 'VA', label: 'Valeur actuelle', syntax: '=VA(taux;nb;versement)', category: 'Financier' },
  { name: 'NB.VPM', label: 'Nombre de périodes', syntax: '=NB.VPM(taux;versement;capital)', category: 'Financier' },
  { name: 'AMORLIN', label: 'Amortissement linéaire', syntax: '=AMORLIN(valeur;résiduel;durée)', category: 'Financier' },
];